import { useState } from "react";
import { X } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { REACTIONS, REACTION_BY_KEY } from "../../utils/reactions";
import { roleLabel } from "../../utils/feedHelpers";

function initialsFor(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return ((parts[0] || "?")[0] || "?").toUpperCase();
}

// Props:
//   reactions: { [userId]: reactionKey }  — the post's reaction map
//   initialKey: reaction key              — which list to show first
//   onClose: () => void
function ReactionsModal({ reactions, initialKey, onClose }) {
  const [{ users }] = useStateValue();
  const [activeKey, setActiveKey] = useState(initialKey || "like");

  const userIds = Object.entries(reactions || {})
    .filter(([, key]) => key === activeKey)
    .map(([uid]) => uid);

  const people = userIds.map((uid) => {
    const u = Array.isArray(users) ? users.find((x) => x.id === uid) : null;
    const name = u ? `${u.firstname || ""} ${u.lastname || ""}`.trim() : "Unknown";
    return {
      id: uid,
      name: name || "Unknown",
      role: u?.role || "",
      photo: u?.profilepicture || "",
    };
  });

  const active = REACTION_BY_KEY[activeKey];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="text-[14px] font-semibold text-slate-800">
            Reactions
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex gap-1 px-3 py-2 border-b border-gray-100 flex-wrap">
          {REACTIONS.map(({ key, label, Icon }) => {
            const n = Object.values(reactions || {}).filter((k) => k === key).length;
            const isActive = key === activeKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveKey(key)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium border ${
                  isActive
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <Icon size={13} />
                <span>{n}</span>
              </button>
            );
          })}
        </div>

        <div className="overflow-y-auto px-4 py-3 space-y-3">
          <div className="text-[12px] font-semibold text-gray-500">
            {active?.label} · {people.length}
          </div>
          {people.length === 0 && (
            <div className="text-[13px] text-gray-400">No one yet.</div>
          )}
          {people.map((p) => (
            <div key={p.id} className="flex items-center gap-3">
              {p.photo ? (
                <img
                  src={p.photo}
                  alt={p.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-700 text-white text-[11px] font-bold flex items-center justify-center">
                  {initialsFor(p.name)}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-slate-800 truncate">
                  {p.name}
                </div>
                {p.role && (
                  <div className="text-[11px] text-gray-400">
                    {roleLabel(p.role)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReactionsModal;
